import { FC } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FiCheck } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '@utils/types';

const BOOKING_FLOW_PATHS = [
  '/select-city',
  '/card-details',
  '/booking',
  '/boarding-pass',
];

const StepProgressBar: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { loungeId } = useParams<{ loungeId?: string }>();
  const { t: translate } = useTranslation();
  const { boardingPassMandatory } = useSelector(
    (state: RootState) => state.card,
  );
  const search = location.search ?? '';

  const baseSteps = [
    translate('loginPage.steps.selectLounge'),
    translate('loginPage.steps.cardDetails'),
    translate('loginPage.steps.additionalDetails'),
  ];
  const steps = boardingPassMandatory
    ? [...baseSteps, 'Boarding Pass']
    : baseSteps;

  // Determine current step from route (routing as source of truth)
  const getCurrentStep = () => {
    if (location.pathname === '/select-city') return 0;
    if (location.pathname.startsWith('/card-details')) return 1;
    if (location.pathname.startsWith('/booking')) return 2;
    if (boardingPassMandatory && location.pathname.startsWith('/boarding-pass'))
      return 3;
    return -1;
  };

  const currentStep = getCurrentStep();

  // Only show progress bar on booking flow routes
  const isBookingFlow = BOOKING_FLOW_PATHS.some((p) =>
    location.pathname.startsWith(p),
  );
  if (!isBookingFlow || currentStep < 0) {
    return null;
  }

  const handleClick = (index: number) => {
    // Only allow navigation to previous steps
    if (index >= currentStep) return;

    // Step 0: Navigate to select-city (no loungeId needed)
    if (index === 0) {
      navigate(`/select-city${search}`);
      return;
    }

    // Steps 1-2: Require loungeId from URL params
    // If loungeId is missing, do nothing (invalid state - should not happen with proper routing)
    if (!loungeId) {
      return;
    }

    if (index === 1) {
      navigate(`/card-details/${loungeId}${search}`);
    } else if (index === 2) {
      navigate(`/booking/${loungeId}${search}`);
    }
  };

  return (
    <div className="flex w-full items-center justify-between bg-gray-100 px-6 py-6">
      <div className="flex items-center">
        {steps.map((label, index) => {
          const isActive = currentStep === index;
          const isCompleted = currentStep > index;
          return (
            <div key={index} className="flex items-center gap-2">
              <button
                onClick={() => handleClick(index)}
                disabled={!isCompleted}
                className="flex flex-col items-center gap-2 focus:outline-none disabled:cursor-not-allowed lg:flex-row"
              >
                <div
                  className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold transition-colors duration-200 ${
                    isCompleted
                      ? 'bg-primary-500 text-white'
                      : isActive
                        ? 'border-2 border-primary-500 bg-gray-200 text-white'
                        : 'bg-gray-300 text-gray-700'
                  }`}
                >
                  {isCompleted ? <FiCheck size={10} strokeWidth={2} /> : ''}
                </div>
                <span
                  className={`text-xs font-bold transition-colors duration-300 lg:text-sm ${
                    isCompleted
                      ? 'text-primary-600'
                      : isActive
                        ? 'font-semibold text-blue-600'
                        : 'text-gray-500'
                  }`}
                >
                  {label}
                </span>
              </button>
              {index < steps.length - 1 && (
                <div
                  className={`mx-1 h-px w-9 ${
                    isCompleted ? 'bg-primary-600' : 'bg-gray-400'
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StepProgressBar;
